import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const FAQ = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-white/[0.06] rounded-2xl overflow-hidden">
      <button onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-white/[0.02] transition-all">
        <span className="text-sm font-black text-white pr-4">{q}</span>
        <svg className={`w-4 h-4 text-gray-500 shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
      </button>
      {open && (
        <div className="px-5 pb-5 border-t border-white/[0.05] pt-4 animate-in fade-in duration-200">
          <p className="text-gray-400 text-sm leading-relaxed">{a}</p>
        </div>
      )}
    </div>
  );
};

const HelpCenter = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('participants');

  const sections = {
    participants: {
      label: 'For Participants',
      faqs: [
        { q: 'How do I register for an event?', a: 'Browse events on the homepage, click on an event to view details, select your segment, fill in the registration form, send the payment via the displayed mobile banking number, and submit your transaction ID. You\'ll receive a confirmation email.' },
        { q: 'When will I receive my ticket?', a: 'Tickets are issued after the organiser verifies your payment. This typically takes 24-48 hours. You\'ll receive an email with your digital QR code ticket once verified.' },
        { q: 'I didn\'t receive my ticket email. What should I do?', a: 'First check your spam/junk folder. If it\'s not there, log in to EventDeck, go to My Tickets, and check the status. If your status shows "Verified" but you have no email, contact the event organiser or our support team.' },
        { q: 'Can I register for multiple segments of the same event?', a: 'Yes, you can register for multiple segments. Each segment requires a separate registration and payment. You can also use the cart feature to register for multiple segments at once.' },
        { q: 'How does the QR code ticket work?', a: 'Your QR code is a unique identifier for your registration. Event staff will scan it at the gate using EventDeck\'s scanner. Make sure to have it downloaded or accessible on your device before attending.' },
        { q: 'Can I transfer my ticket to someone else?', a: 'Ticket transfers depend on the event organiser\'s policy. Check the Terms & Policies section on the event page. If transfers are enabled, contact the organiser with your request.' },
        { q: 'What happens if I lose my ticket email?', a: 'Log in to your EventDeck account and go to My Tickets. All your verified registrations and QR codes are accessible there at any time.' },
        { q: 'My payment was sent but registration shows pending. Why?', a: 'Payment verification is a manual process done by the organiser. It typically takes 24-48 hours. If it\'s been more than 48 hours, contact the organiser or our support team with your transaction ID.' },
      ]
    },
    organisers: {
      label: 'For Organisers',
      faqs: [
        { q: 'How do I get an organiser account?', a: 'Contact us at organisers@eventdeck.app to set up an organiser account. We\'ll review your request and set up your portal access within 1-2 business days.' },
        { q: 'How do I verify participant payments?', a: 'Go to your Portal, click "Verify Payments" in the sidebar. You\'ll see all pending registrations with their transaction IDs. Click "Verify & Ticket" to approve and automatically send the QR ticket by email.' },
        { q: 'Can I add custom fields to my registration form?', a: 'Yes. In Segment Control, edit your segment and scroll to Form Fields. Click "+ Add Field" to add text, number, file upload, or dropdown fields. Mark fields as required as needed.' },
        { q: 'How do I set up multiple payment methods?', a: 'In the Portal, go to Payment Infrastructure. Enter your bKash, Nagad, and/or Rocket numbers. Participants will see all available methods and you\'ll receive payments to whichever number they use.' },
        { q: 'Can I add multiple organisers to my event?', a: 'Yes. In Event Settings, scroll to Co-Organisers section. Add co-organiser names, logos, and bios. They\'ll appear in the Organised By block on the event page.' },
        { q: 'How do I manage merchandise for my event?', a: 'Go to the Merchandise tab in your Portal. Add items with name, price, stock, and optionally size variants. Participants can order from the Merch tab on your event page.' },
        { q: 'How does the gate scanner work?', a: 'Click "Staff" on the homepage and enter your gate PIN. Scan participant QR codes using your device camera. The system will verify the ticket and mark them as checked in.' },
        { q: 'Can I export my registration data?', a: 'Yes. In the Intelligence tab of your portal, click "CSV" to download all registration data. You can also customise which columns to include.' },
      ]
    },
    payments: {
      label: 'Payments',
      faqs: [
        { q: 'What payment methods are accepted?', a: 'EventDeck currently supports bKash, Nagad, and Rocket for manual payment verification. Some events may also support automatic gateway payments.' },
        { q: 'Is my payment information secure?', a: 'We only collect your mobile banking transaction ID for verification purposes. We never store your actual account numbers, PINs, or passwords.' },
        { q: 'What if I sent the wrong amount?', a: 'Contact the event organiser immediately with your transaction ID and the correct amount. They can manually adjust your registration upon verification.' },
        { q: 'Can I use a promo code?', a: 'If the organiser has set up promo codes for the event, there will be a promo code field on the registration form. Enter the code before submitting to apply the discount.' },
        { q: 'How do refunds work?', a: 'Refunds are processed by the event organiser. Check the event\'s Terms & Policies for their specific refund policy. For disputes, contact refunds@eventdeck.app.' },
      ]
    },
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <nav className="fixed top-0 w-full z-50 bg-black/70 backdrop-blur-xl border-b border-white/[0.05]">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <img
              src="/logo192.png"
              alt="EventDeck"
              className="w-10 h-10 object-contain"
            />
            <h1 className="text-lg font-black tracking-tighter uppercase">EVENT<span className="text-sky-500">DECK</span></h1>
          </div>
          <button onClick={() => navigate(-1)} className="text-[10px] font-black text-gray-500 hover:text-white uppercase tracking-widest transition-colors">← Back</button>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-4 md:px-6 pt-32 pb-24">
        <div className="mb-12">
          <p className="text-[9px] font-black text-sky-500 uppercase tracking-[0.4em] mb-2">Support</p>
          <h1 className="text-4xl font-black uppercase tracking-tighter mb-3">Help Center</h1>
          <p className="text-gray-400 text-sm max-w-lg">Find answers to common questions about EventDeck.</p>
        </div>

        {/* Section tabs */}
        <div className="flex gap-2 mb-8 flex-wrap">
          {Object.entries(sections).map(([key, s]) => (
            <button key={key} onClick={() => setActiveSection(key)}
              className={`px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${activeSection === key ? 'bg-sky-500 text-black' : 'bg-white/5 text-gray-500 border border-white/5 hover:text-white'}`}>
              {s.label}
            </button>
          ))}
        </div>

        <div className="space-y-3 mb-16">
          {sections[activeSection].faqs.map((faq, i) => (
            <FAQ key={i} q={faq.q} a={faq.a} />
          ))}
        </div>

        {/* Still need help */}
        <div className="bg-white/[0.02] border border-white/[0.07] rounded-3xl p-8 text-center">
          <h2 className="text-xl font-black uppercase tracking-tight mb-3">Still Need Help?</h2>
          <p className="text-gray-400 text-sm mb-6">Can't find what you're looking for? Our support team is ready to help.</p>
          <button onClick={() => navigate('/contact')}
            className="bg-sky-500 text-black font-black px-6 py-3 rounded-xl text-[10px] uppercase tracking-widest hover:bg-sky-400 transition-all">
            Contact Support →
          </button>
        </div>
      </main>
    </div>
  );
};

export default HelpCenter;